import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  Box,
  Tabs,
  Tab,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/useApi';
import Swal from 'sweetalert2';
import {
  formatarMoedaParaInput,
  formatarValorOnInput,
  parsearValorFormatado,
} from '../../utils/formatToView';
import {
  recalcularNfcomDados,
  isCampoCalculadoNfcom,
  getIcmsGrupo,
} from '../../utils/nfcomCalculos';

interface EditNfcomFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  notaId: number | null;
}

interface DadosNota {
  infNFCom: {
    versao: string;
    Id: string | null;
    ide: {
      cUF: number;
      tpAmb: number;
      mod: number;
      serie: number;
      nNF: number;
      dhEmi: string;
      tpEmis: number;
      nSiteAutoriz: number;
      cMunFG: string;
      finNFCom: number;
      tpFat: number;
      verProc: string;
      indCessaoMeiosRede?: number;
    };
    emit: {
      CNPJ: string;
      IE: string;
      CRT?: number;
      xNome?: string;
      xFant?: string;
      enderEmit?: {
        xLgr: string;
        nro: string;
        xCpl?: string;
        xBairro: string;
        cMun: string;
        xMun: string;
        CEP: string;
        UF: string;
        fone?: string;
      };
    };
    dest: {
      xNome: string;
      CNPJ?: string | null;
      CPF?: string | null;
      indIEDest: number;
      IE?: string;
      IM?: string | null;
      enderDest: {
        xLgr: string;
        nro: string;
        xCpl?: string | null;
        xBairro: string;
        cMun: string;
        xMun: string;
        CEP: string;
        UF: string;
        cPais: string;
        xPais: string;
      };
    };
    assinante: {
      iCodAssinante: string;
      tpAssinante: number;
      tpServUtil: number;
      nContrato: string;
      dContratoIni?: string | null;
    };
    det: Array<{
      nItem: number;
      prod: {
        cProd: string;
        xProd: string;
        cClass: string;
        CFOP: string;
        uMed: number;
        qFaturada: number;
        vItem: number;
        vDesc: number;
        vOutro: number;
        vProd: number;
      };
      imposto: Record<string, any>;
    }>;
    total: {
      vProd: number;
      ICMSTot: {
        vBC: number;
        vICMS: number;
        vICMSDeson: number;
        vFCP: number;
      };
      vCOFINS: number;
      vPIS: number;
      vFUNTTEL: number;
      vFUST: number;
      vRetTribTot?: {
        vRetPIS: number;
        vRetCofins: number;
        vRetCSLL: number;
        vIRRF: number;
      };
      vDesc: number;
      vOutro: number;
      vNF: number;
      IBSCBSTot?: {
        vBCIBSCBS: number;
        gIBS: {
          gIBSUF: { vDif: number; vDevTrib: number; vIBSUF: number };
          gIBSMun: { vDif: number; vDevTrib: number; vIBSMun: number };
          vIBS: number;
        };
        gCBS: { vDif: number; vDevTrib: number; vCBS: number };
      };
      vTotDFe?: number;
    };
    gFat?: {
      CompetFat: string;
      dVencFat: string;
      dPerUsoIni?: string;
      dPerUsoFim?: string;
      codBarras: string;
      gPIX: {
        urlQRCodePIX: string;
      };
    };
    infAdic: {
      infCpl: string[];
    };
  };
  ambiente: string;
}

const ICMS_GRUPOS = ['ICMS00', 'ICMS90', 'ICMS40', 'ICMSSN'] as const;

function formatDhEmiForInput(dhEmi: string | undefined): string {
  if (!dhEmi) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dhEmi)) return `${dhEmi}T00:00`;
  const d = new Date(dhEmi);
  if (Number.isNaN(d.getTime())) return dhEmi;
  return d.toISOString().slice(0, 16);
}

function formatDateForInput(value: string | undefined | null): string {
  if (!value) return '';
  return value.split('T')[0];
}

interface NfcomMoedaFieldProps {
  label: string;
  value?: number | null;
  onChange?: (value: number) => void;
  readOnly?: boolean;
}

function NfcomMoedaField({ label, value, onChange, readOnly = false, ...textFieldProps }: NfcomMoedaFieldProps & Omit<React.ComponentProps<typeof TextField>, 'label' | 'value' | 'onChange'>) {
  return (
    <TextField
      label={label}
      value={formatarMoedaParaInput(Number(value) || 0)}
      onChange={
        readOnly
          ? undefined
          : (e) => {
            const fmt = formatarValorOnInput(e.target.value);
            onChange?.(parsearValorFormatado(fmt));
          }
      }
      InputProps={{
        readOnly,
        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
      }}
      fullWidth
      size="small"
      {...textFieldProps}
    />
  );
}

/** Imposto padrão por item de det (mesmos valores de novo item) — evita crash quando a API omite nós ou usa outro grupo ICMS. */
function createDefaultDetItemImposto() {
  return {
    ICMS00: {
      CST: '00',
      vBC: 0,
      pICMS: 0,
      vICMS: 0,
    },
    PIS: {
      CST: '49',
      vBC: 0,
      pPIS: 0,
      vPIS: 0,
    },
    COFINS: {
      CST: '49',
      vBC: 0,
      pCOFINS: 0,
      vCOFINS: 0,
    },
    FUST: {
      vBC: 0,
      pFUST: 0,
      vFUST: 0,
    },
    FUNTTEL: {
      vBC: 0,
      pFUNTTEL: 0,
      vFUNTTEL: 0,
    },
    retTrib: {
      vRetPIS: 0,
      vRetCofins: 0,
      vRetCSLL: 0,
      vBCIRRF: 0,
      vIRRF: 0,
    },
  };
}

function mergeDetItemImpostoFromApi(raw: any) {
  const base = createDefaultDetItemImposto();
  if (!raw || typeof raw !== 'object') return base;

  const icmsGrupo = getIcmsGrupo(raw);
  const merged: Record<string, any> = {
    PIS: { ...base.PIS, ...(raw.PIS && typeof raw.PIS === 'object' ? raw.PIS : {}) },
    COFINS: { ...base.COFINS, ...(raw.COFINS && typeof raw.COFINS === 'object' ? raw.COFINS : {}) },
    FUST: { ...base.FUST, ...(raw.FUST && typeof raw.FUST === 'object' ? raw.FUST : {}) },
    FUNTTEL: { ...base.FUNTTEL, ...(raw.FUNTTEL && typeof raw.FUNTTEL === 'object' ? raw.FUNTTEL : {}) },
    retTrib: { ...base.retTrib, ...(raw.retTrib && typeof raw.retTrib === 'object' ? raw.retTrib : {}) },
  };

  ICMS_GRUPOS.forEach((grupo) => {
    if (raw[grupo] && typeof raw[grupo] === 'object') {
      merged[grupo] = { ...raw[grupo] };
    }
  });
  if (!merged[icmsGrupo]) {
    merged[icmsGrupo] = { ...base.ICMS00 };
  }

  if (raw.IBSCBS && typeof raw.IBSCBS === 'object') {
    merged.IBSCBS = JSON.parse(JSON.stringify(raw.IBSCBS));
  }

  return merged;
}

/** Monta o objeto completo a partir de colunas separadas ou dados_nota legado. */
function montarDadosNotaFromNota(nota: any): any | null {
  if (nota?.dados_nota?.infNFCom?.ide || nota?.dados_nota?.infNFCom?.emit) {
    return nota.dados_nota;
  }

  const parseField = (value: any) => {
    if (value == null) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const ide = parseField(nota.ide);
  const emitente = parseField(nota.emitente);
  const destinatario = parseField(nota.destinatario);
  const assinante = parseField(nota.assinante);
  const detalhes = parseField(nota.detalhes);
  const totais = parseField(nota.totais);
  const gFat = parseField(nota.g_fat);
  const complementos = parseField(nota.dados_nota);

  if (!ide && !emitente && !destinatario && !assinante && !detalhes && !totais && !gFat) {
    return nota.dados_nota ?? null;
  }

  const infComplemento = complementos?.infNFCom || {};
  return {
    infNFCom: {
      versao: infComplemento.versao ?? '1.00',
      Id: infComplemento.Id ?? null,
      ide: ide ?? complementos?.infNFCom?.ide,
      emit: emitente ?? complementos?.infNFCom?.emit,
      dest: destinatario ?? complementos?.infNFCom?.dest,
      assinante: assinante ?? complementos?.infNFCom?.assinante,
      det: detalhes ?? complementos?.infNFCom?.det,
      total: totais ?? infComplemento.total,
      gFat: gFat ?? infComplemento.gFat,
      infAdic: infComplemento.infAdic,
    },
    ambiente: complementos?.ambiente || nota.ambiente || 'producao',
  };
}

function normalizeNfcomDadosNota(dados: any) {
  if (!dados?.infNFCom?.det || !Array.isArray(dados.infNFCom.det)) return dados;
  const out = JSON.parse(JSON.stringify(dados));
  out.infNFCom.det = out.infNFCom.det
    .filter((row: any) => row != null)
    .map((row: any) => ({
      ...row,
      imposto: mergeDetItemImpostoFromApi(row?.imposto),
    }));
  return out;
}

type FormTabId =
  | 'identificacao'
  | 'emitente'
  | 'destinatario'
  | 'assinante'
  | 'itens'
  | 'totais'
  | 'faturamento'
  | 'infAdic';

const EditNfcomForm: React.FC<EditNfcomFormProps> = ({
  open,
  onClose,
  onSuccess,
  notaId,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dadosNota, setDadosNota] = useState<DadosNota | null>(null);
  const [dadosBrutos, setDadosBrutos] = useState<any>(null);
  const [formTab, setFormTab] = useState<FormTabId>('identificacao');
  const [itemTabIndex, setItemTabIndex] = useState(0);

  useEffect(() => {
    if (open && notaId) {
      setFormTab('identificacao');
      setItemTabIndex(0);
      carregarDadosNota();
    } else {
      setDadosNota(null);
      setError(null);
      setDadosBrutos(null);
    }
  }, [open, notaId]);

  useEffect(() => {
    const len = dadosNota?.infNFCom?.det?.length ?? 0;
    if (len === 0) {
      setItemTabIndex(0);
      return;
    }
    if (itemTabIndex > len - 1) {
      setItemTabIndex(len - 1);
    }
  }, [dadosNota?.infNFCom?.det?.length, itemTabIndex]);

  useEffect(() => {
    if (formTab === 'faturamento' && !dadosNota?.infNFCom?.gFat) {
      setFormTab('totais');
    }
  }, [formTab, dadosNota?.infNFCom?.gFat]);

  const carregarDadosNota = async () => {
    if (!notaId) return;

    try {
      setLoadingData(true);
      setError(null);
      const response = await api.get(`/notas-nfcom/${notaId}`);
      const body = response.data as { success?: boolean; data?: any; error?: string };
      const nota =
        body?.success === true && body.data !== undefined
          ? body.data
          : body?.data ?? body;

      const dadosNotaMontado = montarDadosNotaFromNota(nota);

      if (dadosNotaMontado) {
        let dadosNotaParsed: any = dadosNotaMontado;

        // Se dados_nota for uma string, tentar fazer parse
        if (typeof dadosNotaParsed === 'string') {
          try {
            dadosNotaParsed = JSON.parse(dadosNotaParsed);
          } catch (parseError) {
            console.error('Erro ao fazer parse do JSON:', parseError);
            setError('Erro ao processar dados_nota: formato JSON inválido');
            return;
          }
        }

        // Verificar se tem a estrutura esperada
        // Pode ser que venha direto com infNFCom ou que venha dentro de outro objeto
        if (dadosNotaParsed.infNFCom) {
          // Estrutura correta: { infNFCom: {...}, ambiente: "..." }
          setDadosNota(recalcularNfcomDados(normalizeNfcomDadosNota(dadosNotaParsed)));
        } else if (dadosNotaParsed.infNFCom === undefined && Object.keys(dadosNotaParsed).length > 0) {
          // Pode ser que os dados estejam em um formato diferente
          // Tentar encontrar infNFCom em qualquer nível
          console.warn('Estrutura diferente do esperado, tentando adaptar...');
          console.log('Chaves encontradas:', Object.keys(dadosNotaParsed));

          // Se não tiver infNFCom, pode ser que os dados estejam no nível raiz
          // Nesse caso, vamos criar a estrutura esperada
          if (dadosNotaParsed.versao || dadosNotaParsed.ide) {
            // Parece que os dados estão no nível raiz, vamos envolver em infNFCom
            setDadosNota(
              recalcularNfcomDados(
                normalizeNfcomDadosNota({
                  infNFCom: dadosNotaParsed,
                  ambiente: dadosNotaParsed.ambiente || 'producao',
                })
              )
            );
          } else {
            // Salvar dados brutos para debug
            setDadosBrutos(dadosNotaParsed);
            setError('Estrutura de dados_nota inválida: campo infNFCom não encontrado. Chaves disponíveis: ' + Object.keys(dadosNotaParsed).join(', '));
            console.error('Estrutura inválida:', dadosNotaParsed);
          }
        } else {
          setError('Estrutura de dados_nota inválida: campo infNFCom não encontrado');
          console.error('Estrutura inválida:', dadosNotaParsed);
        }
      } else {
        setError('Nota não possui dados para editar (ide, emitente, destinatário, assinante ou detalhes)');
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados da nota:', err);
      setError(err?.response?.data?.error || 'Erro ao carregar dados da nota');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (path: string, value: any) => {
    if (!dadosNota) return;
    if (isCampoCalculadoNfcom(path)) return;

    const keys = path.split('.');
    const newData = JSON.parse(JSON.stringify(dadosNota));

    let current: any = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      if (key.match(/^\d+$/)) {
        const idx = parseInt(key, 10);
        if (current[idx] == null) current[idx] = {};
        current = current[idx];
      } else {
        if (current[key] == null) {
          current[key] = nextKey.match(/^\d+$/) ? [] : {};
        }
        current = current[key];
      }
    }

    const lastKey = keys[keys.length - 1];
    if (lastKey.match(/^\d+$/)) {
      current[parseInt(lastKey, 10)] = value;
    } else {
      current[lastKey] = value;
    }

    setDadosNota(recalcularNfcomDados(newData));
  };

  const handleAdicionarItem = () => {
    if (!dadosNota?.infNFCom) return;

    const newData = JSON.parse(JSON.stringify(dadosNota));
    const baseImposto =
      newData.infNFCom.det?.[0]?.imposto
        ? mergeDetItemImpostoFromApi(JSON.parse(JSON.stringify(newData.infNFCom.det[0].imposto)))
        : createDefaultDetItemImposto();

    const novoItem = {
      nItem: (newData.infNFCom.det?.length || 0) + 1,
      prod: {
        cProd: `${(newData.infNFCom.det?.length || 0) + 1}`,
        xProd: '',
        cClass: '0100401',
        CFOP: '5307',
        uMed: 4,
        qFaturada: 1,
        vItem: 0,
        vDesc: 0,
        vOutro: 0,
        vProd: 0,
      },
      imposto: baseImposto,
    };

    if (!newData.infNFCom.det) {
      newData.infNFCom.det = [];
    }
    newData.infNFCom.det.push(novoItem);

    // Atualizar números dos itens
    newData.infNFCom.det.forEach((item: any, index: number) => {
      item.nItem = index + 1;
      item.prod.cProd = `${index + 1}`;
    });

    setDadosNota(recalcularNfcomDados(newData));
  };

  const handleRemoverItem = async (index: number) => {
    if (!dadosNota?.infNFCom?.det) return;

    // Verificar se é o primeiro item (item 1)
    if (index === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'Não é possível remover',
        text: 'O primeiro item (Item 1) não pode ser removido. Apenas os demais itens podem ser removidos.',
        customClass: {
          popup: 'swal-zindex',
          container: 'swal-zindex',
        },
      });
      return;
    }

    const item = dadosNota.infNFCom.det[index];
    const confirmacao = await Swal.fire({
      title: 'Confirmar remoção?',
      text: `Deseja realmente remover o item ${item.nItem} - ${item.prod?.xProd || 'sem descrição'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
      customClass: {
        popup: 'swal-zindex',
        container: 'swal-zindex',
      },
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    const newData = JSON.parse(JSON.stringify(dadosNota));
    newData.infNFCom.det.splice(index, 1);

    // Atualizar números dos itens
    newData.infNFCom.det.forEach((item: any, idx: number) => {
      item.nItem = idx + 1;
      item.prod.cProd = `${idx + 1}`;
    });

    setDadosNota(recalcularNfcomDados(newData));
  };

  const handleSubmit = async () => {
    if (!dadosNota || !notaId) return;

    try {
      setLoading(true);
      setError(null);

      const dadosSalvar = recalcularNfcomDados(dadosNota);

      await api.put(`/notas-nfcom/${notaId}/dados-nota`, {
        dados_nota: dadosSalvar,
      });

      Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: 'Dados da nota atualizados com sucesso',
        customClass: {
          popup: 'swal-zindex',
          container: 'swal-zindex',
        },
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao atualizar dados da nota:', err);
      setError(err?.response?.data?.error || 'Erro ao atualizar dados da nota');
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: err?.response?.data?.error || 'Erro ao atualizar dados da nota',
        customClass: {
          popup: 'swal-zindex',
          container: 'swal-zindex',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (!dadosNota || !dadosNota.infNFCom) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Editar Dados da NFCom</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              {dadosBrutos && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Dados brutos recebidos (para debug):
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '400px',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(dadosBrutos, null, 2)}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">Carregando dados da nota...</Alert>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Editar Dados da NFCom</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={formTab}
              onChange={(_, v: FormTabId) => setFormTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Identificação" value="identificacao" />
              <Tab label="Emitente" value="emitente" />
              <Tab label="Destinatário" value="destinatario" />
              <Tab label="Assinante" value="assinante" />
              <Tab
                label={`Itens (${dadosNota?.infNFCom?.det?.length ?? 0})`}
                value="itens"
              />
              <Tab label="Totais" value="totais" />
              {dadosNota?.infNFCom?.gFat ? (
                <Tab label="Faturamento" value="faturamento" />
              ) : null}
              <Tab label="Informações adicionais" value="infAdic" />
            </Tabs>
          </Box>

          {formTab === 'identificacao' && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Versão"
                    value={dadosNota?.infNFCom?.versao || ''}
                    onChange={(e) => handleChange('infNFCom.versao', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Id (XML)"
                    value={dadosNota?.infNFCom?.Id ?? ''}
                    onChange={(e) => handleChange('infNFCom.Id', e.target.value || null)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Código UF"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.cUF || ''}
                    onChange={(e) => handleChange('infNFCom.ide.cUF', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tipo Ambiente"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.tpAmb || ''}
                    onChange={(e) => handleChange('infNFCom.ide.tpAmb', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Modelo"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.mod || ''}
                    onChange={(e) => handleChange('infNFCom.ide.mod', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Série"
                    type="text"
                    value={dadosNota?.infNFCom?.ide?.serie || ''}
                    onChange={(e) => handleChange('infNFCom.ide.serie', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Número NF"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.nNF || ''}
                    onChange={(e) => handleChange('infNFCom.ide.nNF', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data/Hora Emissão"
                    type="datetime-local"
                    value={formatDhEmiForInput(dadosNota?.infNFCom?.ide?.dhEmi)}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange(
                        'infNFCom.ide.dhEmi',
                        v.includes('T') ? new Date(v).toISOString() : v
                      );
                    }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tipo Emissão"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.tpEmis || ''}
                    onChange={(e) => handleChange('infNFCom.ide.tpEmis', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Site Autorização"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.nSiteAutoriz || ''}
                    onChange={(e) => handleChange('infNFCom.ide.nSiteAutoriz', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Código Município FG"
                    value={dadosNota?.infNFCom?.ide?.cMunFG || ''}
                    onChange={(e) => handleChange('infNFCom.ide.cMunFG', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Finalidade NFCom"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.finNFCom || ''}
                    onChange={(e) => handleChange('infNFCom.ide.finNFCom', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tipo Faturamento"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.tpFat || ''}
                    onChange={(e) => handleChange('infNFCom.ide.tpFat', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Versão Processo"
                    value={dadosNota?.infNFCom?.ide?.verProc || ''}
                    onChange={(e) => handleChange('infNFCom.ide.verProc', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Ind. Cessão Meios Rede"
                    type="number"
                    value={dadosNota?.infNFCom?.ide?.indCessaoMeiosRede || ''}
                    onChange={(e) => handleChange('infNFCom.ide.indCessaoMeiosRede', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'emitente' && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CNPJ"
                    value={dadosNota?.infNFCom?.emit?.CNPJ || ''}
                    onChange={(e) => handleChange('infNFCom.emit.CNPJ', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Inscrição Estadual"
                    value={dadosNota?.infNFCom?.emit?.IE || ''}
                    onChange={(e) => handleChange('infNFCom.emit.IE', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="CRT"
                    type="number"
                    value={dadosNota?.infNFCom?.emit?.CRT ?? ''}
                    onChange={(e) => handleChange('infNFCom.emit.CRT', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Razão Social"
                    value={dadosNota?.infNFCom?.emit?.xNome || ''}
                    onChange={(e) => handleChange('infNFCom.emit.xNome', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Nome Fantasia"
                    value={dadosNota?.infNFCom?.emit?.xFant || ''}
                    onChange={(e) => handleChange('infNFCom.emit.xFant', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Endereço do emitente
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Logradouro"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.xLgr || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.xLgr', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Número"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.nro || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.nro', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Complemento"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.xCpl || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.xCpl', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Bairro"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.xBairro || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.xBairro', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Código Município"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.cMun || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.cMun', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Município"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.xMun || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.xMun', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="CEP"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.CEP || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.CEP', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="UF"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.UF || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.UF', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Telefone"
                    value={dadosNota?.infNFCom?.emit?.enderEmit?.fone || ''}
                    onChange={(e) => handleChange('infNFCom.emit.enderEmit.fone', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'destinatario' && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nome/Razão Social"
                    value={dadosNota?.infNFCom?.dest.xNome || ''}
                    onChange={(e) => handleChange('infNFCom.dest.xNome', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CNPJ"
                    value={dadosNota?.infNFCom?.dest.CNPJ || ''}
                    onChange={(e) => handleChange('infNFCom.dest.CNPJ', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CPF"
                    value={dadosNota?.infNFCom?.dest.CPF || ''}
                    onChange={(e) => handleChange('infNFCom.dest.CPF', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Ind. IE Destinatário"
                    type="number"
                    value={dadosNota?.infNFCom?.dest.indIEDest || ''}
                    onChange={(e) => handleChange('infNFCom.dest.indIEDest', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Inscrição Estadual"
                    value={dadosNota?.infNFCom?.dest.IE || ''}
                    onChange={(e) => handleChange('infNFCom.dest.IE', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Inscrição Municipal"
                    value={dadosNota?.infNFCom?.dest.IM || ''}
                    onChange={(e) => handleChange('infNFCom.dest.IM', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Divider sx={{ width: '100%', my: 2 }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Endereço
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Logradouro"
                    value={dadosNota?.infNFCom?.dest.enderDest.xLgr || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.xLgr', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Número"
                    value={dadosNota?.infNFCom?.dest.enderDest.nro || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.nro', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Complemento"
                    value={dadosNota?.infNFCom?.dest.enderDest.xCpl || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.xCpl', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Bairro"
                    value={dadosNota?.infNFCom?.dest.enderDest.xBairro || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.xBairro', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Código Município"
                    value={dadosNota?.infNFCom?.dest.enderDest.cMun || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.cMun', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nome Município"
                    value={dadosNota?.infNFCom?.dest.enderDest.xMun || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.xMun', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="CEP"
                    value={dadosNota?.infNFCom?.dest.enderDest.CEP || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.CEP', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="UF"
                    value={dadosNota?.infNFCom?.dest.enderDest.UF || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.UF', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Código País"
                    value={dadosNota?.infNFCom?.dest.enderDest.cPais || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.cPais', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nome País"
                    value={dadosNota?.infNFCom?.dest.enderDest.xPais || ''}
                    onChange={(e) => handleChange('infNFCom.dest.enderDest.xPais', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'assinante' && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Código Assinante"
                    value={dadosNota?.infNFCom?.assinante.iCodAssinante || ''}
                    onChange={(e) => handleChange('infNFCom.assinante.iCodAssinante', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tipo Assinante"
                    type="number"
                    value={dadosNota?.infNFCom?.assinante.tpAssinante || ''}
                    onChange={(e) => handleChange('infNFCom.assinante.tpAssinante', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Tipo Serviço Utilizado"
                    type="number"
                    value={dadosNota?.infNFCom?.assinante.tpServUtil || ''}
                    onChange={(e) => handleChange('infNFCom.assinante.tpServUtil', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Número Contrato"
                    value={dadosNota?.infNFCom?.assinante.nContrato || ''}
                    onChange={(e) => handleChange('infNFCom.assinante.nContrato', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Início Contrato"
                    type="date"
                    value={formatDateForInput(dadosNota?.infNFCom?.assinante.dContratoIni)}
                    onChange={(e) => handleChange('infNFCom.assinante.dContratoIni', e.target.value || null)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'itens' && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Informe quantidade, valor unitário, desconto e alíquotas. Os impostos e totais são recalculados automaticamente.
              </Alert>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">
                  Itens da nota ({dadosNota?.infNFCom?.det?.length || 0})
                </Typography>
                <IconButton
                  onClick={() => {
                    handleAdicionarItem();
                    const nextLen = (dadosNota?.infNFCom?.det?.length || 0) + 1;
                    setItemTabIndex(nextLen - 1);
                  }}
                  color="primary"
                  size="small"
                  title="Adicionar item"
                >
                  <AddIcon />
                </IconButton>
              </Box>
              {dadosNota?.infNFCom?.det?.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Nenhum item adicionado
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAdicionarItem}
                    sx={{ mt: 1 }}
                  >
                    Adicionar Item
                  </Button>
                </Box>
              )}
              {(dadosNota?.infNFCom?.det?.length ?? 0) > 0 && (
                <>
                  <Tabs
                    value={itemTabIndex}
                    onChange={(_, v) => setItemTabIndex(Number(v))}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                    {dadosNota.infNFCom.det.map((item, index) => (
                      <Tab
                        key={index}
                        value={index}
                        label={item.prod?.xProd ? `Item ${item.nItem}` : `Item ${item.nItem} (novo)`}
                      />
                    ))}
                  </Tabs>
                  {dadosNota.infNFCom.det.map((item, index) =>
                    itemTabIndex === index ? (
                      <Box key={index}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary">
                            {item.prod?.xProd || 'Novo item'} — Item {item.nItem}
                          </Typography>
                          <IconButton
                            onClick={() => handleRemoverItem(index)}
                            color="error"
                            size="small"
                            disabled={index === 0}
                            title={
                              index === 0
                                ? 'O primeiro item (Item 1) não pode ser removido'
                                : 'Remover item'
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Produto
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Código Produto"
                              value={item.prod.cProd || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.cProd`, e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={9}>
                            <TextField
                              label="Descrição"
                              value={item.prod.xProd || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.xProd`, e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Classificação"
                              value={item.prod.cClass || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.cClass`, e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="CFOP"
                              value={item.prod.CFOP || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.CFOP`, e.target.value)}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Unidade Medida"
                              type="number"
                              value={item.prod.uMed || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.uMed`, Number(e.target.value))}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Quantidade"
                              type="number"
                              value={item.prod.qFaturada || ''}
                              onChange={(e) => handleChange(`infNFCom.det.${index}.prod.qFaturada`, Number(e.target.value))}
                              fullWidth
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <NfcomMoedaField
                              label="Valor Produto"
                              value={item.prod.vProd}
                              onChange={(v) => handleChange(`infNFCom.det.${index}.prod.vProd`, v)}
                              readOnly
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <NfcomMoedaField
                              label="Desconto"
                              value={item.prod.vDesc}
                              onChange={(v) => handleChange(`infNFCom.det.${index}.prod.vDesc`, v)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <NfcomMoedaField
                              label="Outros"
                              value={item.prod.vOutro}
                              onChange={(v) => handleChange(`infNFCom.det.${index}.prod.vOutro`, v)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <NfcomMoedaField
                              label="Valor unitário"
                              value={item.prod.vItem}
                              onChange={(v) => handleChange(`infNFCom.det.${index}.prod.vItem`, v)}
                            />
                          </Grid>
                          <Divider sx={{ width: '100%', my: 2 }} />
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Impostos
                            </Typography>
                          </Grid>
                          {(() => {
                            const icmsGrupo = getIcmsGrupo(item.imposto);
                            const icms = item.imposto[icmsGrupo] || {};
                            const icmsPrefix = `infNFCom.det.${index}.imposto.${icmsGrupo}`;
                            const ibscbs = item.imposto.IBSCBS;
                            const gIbs = ibscbs?.gIBSCBS;
                            return (
                              <>
                                <Grid item xs={12}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    ICMS ({icmsGrupo})
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="ICMS - CST"
                                    value={icms.CST || ''}
                                    onChange={(e) => handleChange(`${icmsPrefix}.CST`, e.target.value)}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                {icmsGrupo !== 'ICMS40' && (
                                  <>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <NfcomMoedaField
                                        label="ICMS - Base Cálculo"
                                        value={icms.vBC}
                                        readOnly
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <TextField
                                        label="ICMS - Alíquota"
                                        type="number"
                                        value={icms.pICMS ?? ''}
                                        onChange={(e) => handleChange(`${icmsPrefix}.pICMS`, Number(e.target.value))}
                                        fullWidth
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <NfcomMoedaField
                                        label="ICMS - Valor"
                                        value={icms.vICMS}
                                        readOnly
                                      />
                                    </Grid>
                                  </>
                                )}
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="PIS - CST"
                                    value={item.imposto.PIS?.CST || ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.PIS.CST`, e.target.value)}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="PIS - Base Cálculo"
                                    value={item.imposto.PIS?.vBC}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="PIS - Alíquota"
                                    type="number"
                                    value={item.imposto.PIS?.pPIS ?? ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.PIS.pPIS`, Number(e.target.value))}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="PIS - Valor"
                                    value={item.imposto.PIS?.vPIS}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="COFINS - CST"
                                    value={item.imposto.COFINS?.CST || ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.COFINS.CST`, e.target.value)}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="COFINS - Base Cálculo"
                                    value={item.imposto.COFINS?.vBC}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="COFINS - Alíquota"
                                    type="number"
                                    value={item.imposto.COFINS?.pCOFINS ?? ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.COFINS.pCOFINS`, Number(e.target.value))}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="COFINS - Valor"
                                    value={item.imposto.COFINS?.vCOFINS}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="FUST - Base Cálculo"
                                    value={item.imposto.FUST?.vBC}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="FUST - Alíquota"
                                    type="number"
                                    value={item.imposto.FUST?.pFUST ?? ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.FUST.pFUST`, Number(e.target.value))}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="FUST - Valor"
                                    value={item.imposto.FUST?.vFUST}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="FUNTTEL - Base Cálculo"
                                    value={item.imposto.FUNTTEL?.vBC}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="FUNTTEL - Alíquota"
                                    type="number"
                                    value={item.imposto.FUNTTEL?.pFUNTTEL ?? ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.FUNTTEL.pFUNTTEL`, Number(e.target.value))}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="FUNTTEL - Valor"
                                    value={item.imposto.FUNTTEL?.vFUNTTEL}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    Retenções tributárias (retTrib)
                                  </Typography>
                                </Grid>
                                {(['vRetPIS', 'vRetCofins', 'vRetCSLL', 'vBCIRRF', 'vIRRF'] as const).map((campo) => (
                                  <Grid item xs={12} sm={6} md={3} key={campo}>
                                    <NfcomMoedaField
                                      label={campo}
                                      value={item.imposto.retTrib?.[campo]}
                                      readOnly
                                    />
                                  </Grid>
                                ))}
                                <Grid item xs={12}>
                                  <Divider sx={{ my: 1 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    IBS / CBS (Reforma Tributária)
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="IBSCBS - CST"
                                    value={ibscbs?.CST || ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.IBSCBS.CST`, e.target.value)}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="IBSCBS - cClassTrib"
                                    value={ibscbs?.cClassTrib || ''}
                                    onChange={(e) => handleChange(`infNFCom.det.${index}.imposto.IBSCBS.cClassTrib`, e.target.value)}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="IBSCBS - Base Cálculo"
                                    value={gIbs?.vBC}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="IBS UF - Alíquota"
                                    type="number"
                                    value={gIbs?.gIBSUF?.pIBSUF ?? ''}
                                    onChange={(e) =>
                                      handleChange(`infNFCom.det.${index}.imposto.IBSCBS.gIBSCBS.gIBSUF.pIBSUF`, Number(e.target.value))
                                    }
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="IBS UF - Valor"
                                    value={gIbs?.gIBSUF?.vIBSUF}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="IBS Mun - Alíquota"
                                    type="number"
                                    value={gIbs?.gIBSMun?.pIBSMun ?? ''}
                                    onChange={(e) =>
                                      handleChange(`infNFCom.det.${index}.imposto.IBSCBS.gIBSCBS.gIBSMun.pIBSMun`, Number(e.target.value))
                                    }
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="IBS Mun - Valor"
                                    value={gIbs?.gIBSMun?.vIBSMun}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="IBS - Valor Total"
                                    value={gIbs?.vIBS}
                                    readOnly
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <TextField
                                    label="CBS - Alíquota"
                                    type="number"
                                    value={gIbs?.gCBS?.pCBS ?? ''}
                                    onChange={(e) =>
                                      handleChange(`infNFCom.det.${index}.imposto.IBSCBS.gIBSCBS.gCBS.pCBS`, Number(e.target.value))
                                    }
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                  <NfcomMoedaField
                                    label="CBS - Valor"
                                    value={gIbs?.gCBS?.vCBS}
                                    readOnly
                                  />
                                </Grid>
                              </>
                            );
                          })()}
                        </Grid>
                      </Box>
                    ) : null
                  )}
                </>
              )}
            </Box>
          )}

          {formTab === 'totais' && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Os totais são calculados automaticamente com base nos itens e alíquotas informados.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Valor Produtos" value={dadosNota?.infNFCom?.total.vProd} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="ICMS - Base Cálculo" value={dadosNota?.infNFCom?.total.ICMSTot.vBC} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="ICMS - Valor" value={dadosNota?.infNFCom?.total.ICMSTot.vICMS} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="ICMS - Desonerado" value={dadosNota?.infNFCom?.total.ICMSTot.vICMSDeson} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="ICMS - FCP" value={dadosNota?.infNFCom?.total.ICMSTot.vFCP} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="COFINS - Valor" value={dadosNota?.infNFCom?.total.vCOFINS} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="PIS - Valor" value={dadosNota?.infNFCom?.total.vPIS} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="FUNTTEL - Valor" value={dadosNota?.infNFCom?.total.vFUNTTEL} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="FUST - Valor" value={dadosNota?.infNFCom?.total.vFUST} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Desconto" value={dadosNota?.infNFCom?.total.vDesc} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Outros" value={dadosNota?.infNFCom?.total.vOutro} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Valor Total NF" value={dadosNota?.infNFCom?.total.vNF} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Valor Total DFe (vTotDFe)" value={dadosNota?.infNFCom?.total.vTotDFe} readOnly />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Retenções totais (vRetTribTot)</Typography>
                </Grid>
                {(['vRetPIS', 'vRetCofins', 'vRetCSLL', 'vIRRF'] as const).map((campo) => (
                  <Grid item xs={12} sm={6} md={3} key={campo}>
                    <NfcomMoedaField
                      label={campo}
                      value={dadosNota?.infNFCom?.total.vRetTribTot?.[campo]}
                      readOnly
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">IBS / CBS totais (IBSCBSTot)</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="Base IBSCBS" value={dadosNota?.infNFCom?.total.IBSCBSTot?.vBCIBSCBS} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="IBS UF - Valor" value={dadosNota?.infNFCom?.total.IBSCBSTot?.gIBS?.gIBSUF?.vIBSUF} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="IBS Mun - Valor" value={dadosNota?.infNFCom?.total.IBSCBSTot?.gIBS?.gIBSMun?.vIBSMun} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="IBS - Valor Total" value={dadosNota?.infNFCom?.total.IBSCBSTot?.gIBS?.vIBS} readOnly />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <NfcomMoedaField label="CBS - Valor" value={dadosNota?.infNFCom?.total.IBSCBSTot?.gCBS?.vCBS} readOnly />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'faturamento' && dadosNota?.infNFCom?.gFat && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Competência Faturamento"
                    value={dadosNota?.infNFCom?.gFat?.CompetFat || ''}
                    onChange={(e) => handleChange('infNFCom.gFat.CompetFat', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Data Vencimento"
                    type="date"
                    value={formatDateForInput(dadosNota?.infNFCom?.gFat?.dVencFat)}
                    onChange={(e) => handleChange('infNFCom.gFat.dVencFat', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Período Uso - Início"
                    type="date"
                    value={formatDateForInput(dadosNota?.infNFCom?.gFat?.dPerUsoIni)}
                    onChange={(e) => handleChange('infNFCom.gFat.dPerUsoIni', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Período Uso - Fim"
                    type="date"
                    value={formatDateForInput(dadosNota?.infNFCom?.gFat?.dPerUsoFim)}
                    onChange={(e) => handleChange('infNFCom.gFat.dPerUsoFim', e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Código de Barras"
                    value={dadosNota?.infNFCom?.gFat?.codBarras || ''}
                    onChange={(e) => handleChange('infNFCom.gFat.codBarras', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="PIX - URL QR Code"
                    value={dadosNota?.infNFCom?.gFat?.gPIX?.urlQRCodePIX || ''}
                    onChange={(e) => handleChange('infNFCom.gFat.gPIX.urlQRCodePIX', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {formTab === 'infAdic' && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Informações Complementares"
                    value={dadosNota?.infNFCom?.infAdic?.infCpl?.join('\n') || ''}
                    onChange={(e) => handleChange('infNFCom.infAdic.infCpl', e.target.value.split('\n'))}
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Ambiente"
                    value={dadosNota?.ambiente || ''}
                    onChange={(e) => handleChange('ambiente', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNfcomForm;

